import { Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import FoldedHandsSvg from '../../../assets/icons/FoldedHandsSvg';
import TranslateSvg from '../../../assets/icons/TranslateSvg';
import theme from '../../../assets/theme';
import { useTranslateSlides } from '../../../hooks/useTranslateSlides';
import { LessonAttachment } from '../../../services/backendService/backendService';
import { notUndefined } from '../../../utils/general';
import { allLanguages } from '../../../utils/language-list';
import Select from '../../_atoms/_Select/Select';
import { Button } from '../../_atoms/Button';
import { LoadingDots } from '../../_atoms/DismissableQuestionList/DismissableQuestionList';
import { IconWrapper } from '../../_atoms/IconWrapper';
import TranslateMultiSelect, {
  SlidesToTranslate,
} from '../../_atoms/TranslateMultiSelect/TranslateMultiSelect';
import { Slide } from '../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../SlideComponents/slideUtil';

const options: {
  displayName: string;
  value: string | number | boolean;
}[] = [
  { value: 'automatic', displayName: 'Automatically detect' },
  ...Object.values(allLanguages).map(lang => ({
    value: lang.int,
    displayName: lang.int,
  })),
];

const TranslateTab = ({
  slides,
  lessonAttachments,
  onAddTranslatedSlides,
  selectedSlide,
}: {
  slides: Slide[];
  selectedSlide?: Slide;
  lessonAttachments?: LessonAttachment[];
  onAddTranslatedSlides: (
    newSlidesAndAttachments: { slides: Slide[]; lessonAttachments: LessonAttachment[] },
    slidesToTranslate: SlidesToTranslate,
  ) => void;
}) => {
  const { t } = useTranslation('Host');
  const [originLanguage, setOriginLanguage] = useState('automatic');
  const [outputLanguage, setOutputLanguage] = useState('Spanish');
  const [slidesToTranslate, setSlidesToTranslate] = useState<SlidesToTranslate>([]);
  useEffect(() => {
    // Look for previously used output language in localstorage as a default value;
    const data = localStorage.getItem('@curipod_v1_ai_translate_preferences_last_used');
    if (data) {
      try {
        const result = JSON.parse(data) as {
          language: string;
          originLanguage: string;
          shouldDuplicate: boolean;
        };
        if (result) {
          setOutputLanguage(result.language);
          setOriginLanguage(result.originLanguage);
        }
      } catch (e) {
        return undefined;
      }
    }
  }, []);

  const { isLoading, translateSlides, reset, error } = useTranslateSlides();

  const handleTranslatedSlidesSuccess = (
    newTranslatedSlidesAndAttachments: {
      slides: Slide[];
      lessonAttachments: LessonAttachment[];
    },
    slidesToTranslate: SlidesToTranslate,
  ) => {
    reset();
    onAddTranslatedSlides(newTranslatedSlidesAndAttachments, slidesToTranslate);
  };

  const handleSetSlidesToTranslate = (newSlidesToTranslate: SlidesToTranslate) => {
    // filter out slides that have no text
    const relevantSlides = newSlidesToTranslate.filter(toTranslate => {
      const slide = slides.find(slide => slide.id === toTranslate.slideId);
      if (!slide) return false;
      return slide.elements.some(element => element.type === 'text' && element.textValue);
    });
    setSlidesToTranslate(relevantSlides);
  };
  const handleTranslate = () => {
    const language = options.find(opt => opt.value === outputLanguage)?.displayName;
    if (slidesToTranslate && slidesToTranslate.length !== 0 && language) {
      const allRelatedSlides: Slide[] = [];

      slidesToTranslate.forEach(slide => {
        const relatedSlides = findRelatedSlidesWithoutIndex(slide.slideId, slides);
        allRelatedSlides.push(...relatedSlides);
      });

      if (allRelatedSlides.length > 0 && language) {
        translateSlides({
          slides: allRelatedSlides,
          lessonAttachments: lessonAttachments || [],
          originLanguage,
          outputLanguage: language,
          onSuccessFunction: data => {
            handleTranslatedSlidesSuccess(data, slidesToTranslate);
          },
        });
      }

      localStorage.setItem(
        '@curipod_v1_ai_translate_preferences_last_used',
        JSON.stringify({ language, originLanguage }),
      );
    }
  };

  const selectableSlides = useMemo(
    () =>
      slides
        .filter(
          r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
        )
        .map((r, i) => {
          if (r.elements.some(e => e.type === 'text' && e.textValue)) {
            return { displayName: `Slide ${i + 1}`, slideId: r.id };
          }
          return { displayName: `Slide ${i + 1}`, slideId: r.id, noContent: true };
        }),
    [slides],
  );
  const isAllSlidesSelected = useMemo(() => {
    const relevantSlides = selectableSlides.filter(slide => slide.noContent !== true);
    if (!slidesToTranslate) return false;
    if (slidesToTranslate.length === 0) return false;
    return slidesToTranslate.length === relevantSlides.length;
  }, [selectableSlides, slidesToTranslate]);

  const handleSelectSlide = (slideId: string) => {
    const slideIndex = selectableSlides.findIndex(r => r.slideId === slideId);

    if (!slidesToTranslate) {
      //If there are no slides selected, add the slide
      handleSetSlidesToTranslate([{ name: `Slide ${slideIndex + 1}`, slideId: slideId }]);
    } else if (slidesToTranslate.some(slide => slide.slideId === slideId)) {
      // If the slide is already selected, remove it
      handleSetSlidesToTranslate(
        slidesToTranslate.filter(slide => slide.slideId !== slideId),
      );
    } else if (slideIndex !== undefined) {
      // If the slide is not selected and has a valid index add it
      handleSetSlidesToTranslate([
        ...slidesToTranslate,
        { name: `Slide ${slideIndex + 1}`, slideId: slideId },
      ]);
    }
  };

  const selectTranslateAllSlides = () => {
    const relavantSlides = selectableSlides.filter(slide => slide.noContent !== true);
    if (relavantSlides.length === slidesToTranslate?.length) {
      handleSetSlidesToTranslate([]);
    } else {
      const allTranslatableSlides = selectableSlides
        .map(r => {
          if (r.noContent) return;
          return { name: r.displayName, slideId: r.slideId };
        })
        .filter(notUndefined);
      handleSetSlidesToTranslate(allTranslatableSlides);
    }
  };

  const { show: showIntercom } = useIntercom();

  return (
    <TabContainer>
      <FormContainer>
        <InputsContainer>
          <Title $fontSize={20}>{t('Translate')}</Title>
          <Select
            style={{
              fontSize: 16,
              width: '100%',
            }}
            showSearch
            filterOption={(input, option) => {
              const val = (option?.value || '')
                .toLowerCase()
                .includes(input.toLowerCase());
              return val;
            }}
            optionFilterProp="children"
            value={originLanguage}
            label={t('Translate from')}
            labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
            onSelect={e => setOriginLanguage(e as string)}
            options={options}
          ></Select>
          <Select
            style={{
              fontSize: 16,
              width: '100%',
            }}
            showSearch
            filterOption={(input, option) => {
              const val = (option?.value || '')
                .toLowerCase()
                .includes(input.toLowerCase());
              return val;
            }}
            optionFilterProp="children"
            value={outputLanguage}
            label={t('Translate to')}
            labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
            onSelect={e => setOutputLanguage(e as string)}
            options={options}
          ></Select>
          <TranslateMultiSelect
            isTranslateAllSlides={isAllSlidesSelected}
            selectTranslateAllSlides={selectTranslateAllSlides}
            slidesToTranslate={slidesToTranslate}
            onSelect={e => handleSelectSlide(e as string)}
            selectableSlides={selectableSlides}
            totalTranslatableSlides={selectableSlides.filter(s => !s.noContent).length}
            selectedSlide={selectedSlide}
          />
        </InputsContainer>
        <div>
          <div>
            <Tooltip
              mouseEnterDelay={0.5}
              title={
                slidesToTranslate?.length === 0
                  ? 'Please select the slides you want to translate'
                  : ''
              }
            >
              <Button
                disabled={slidesToTranslate?.length === 0}
                width="100%"
                loading={isLoading}
                type="primary"
                icon={<TranslateSvg />}
                onClick={() => handleTranslate()}
              >
                {isLoading ? (
                  <span>
                    <span>{t('Translating')}</span>
                    <LoadingDots>...</LoadingDots>
                  </span>
                ) : (
                  <span>{t('Translate')}</span>
                )}
              </Button>{' '}
            </Tooltip>
          </div>

          <p style={{ marginTop: 20 }}>
            {t('Problems with translation?')}{' '}
            <span
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={showIntercom}
            >
              {t(`Please let us know`)}
            </span>
          </p>
        </div>
        {!!error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              width: '100%',
              justifyContent: 'center',
              marginTop: 6,
            }}
          >
            <IconWrapper height="100px" width="100px">
              <FoldedHandsSvg />
            </IconWrapper>
            <p
              style={{
                marginBottom: 0,
                lineHeight: '14px',
                fontWeight: 'bold',
                paddingTop: 10,
              }}
            >
              {t('Failed to translate. Please try again')}
            </p>
          </div>
        )}
      </FormContainer>
    </TabContainer>
  );
};

const TabContainer = styled.div`
  padding-top: 20px;
  height: 100%;
`;

const FormContainer = styled.div`
  padding: 0px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px 20px;
  justify-content: space-between;
  height: 100%;
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px 20px;
  height: 100%;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

export default TranslateTab;
